import React, { useEffect, useState, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootDispatch, RootState } from "../../store";
import {
  fetchStoreStockAdminViewPagination,
  downloadStoreStockReport,
  getStorePriceByWarehouse,
} from "../../store/slices/storeInwardSlice";
import debounce from "lodash/debounce";
import {
  Menu,
  Transition,
  MenuButton,
  MenuItems,
  MenuItem,
} from "@headlessui/react";
import {
  ChevronDownIcon,
  ArrowDownTrayIcon,
  InformationCircleIcon,
} from "@heroicons/react/20/solid";

interface WarehouseStock {
  warehouse: { _id: string; name: string };
  quantity: { value: number; unit: string };
  totalPrice: number;
}

interface WarehouseValue {
  warehouseId: string;
  name: string;
  totalPrice: number;
}

interface RawMaterialStock {
  _id: string;
  name: string;
  sku: string;
  stock: WarehouseStock[];
}

const formatValue = (value: number | undefined | null): string => {
  if (value === undefined || value === null) return "0";

  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  })
    .format(value)
    .replace("INR", "₹");
};

interface Props {
  globalDate: string;
}

const StoreStockPageByWarehouse: React.FC<Props> = ({ globalDate }) => {
  const dispatch = useDispatch<RootDispatch>();

  const {
    rawMaterials,
    loading: warehouseLoading,
    pagination,
    isExporting,
  } = useSelector((state: RootState) => state.storeInward.storeStock);
  const {
    warehouses: warehouseValues,
    grandTotal,
    loading: valueLoading,
  } = useSelector((state: RootState) => state.storeInward.warehouseValues);

  const warehouseProducts = useMemo(() => {
    return (rawMaterials || []).map(
      (item): RawMaterialStock => ({
        _id: item._id,
        name: item.name,
        sku: item.sku || "",
        stock: item.stock || [],
      }),
    );
  }, [rawMaterials]);

  const {
    totalPages: totalWarehousePages,
    currentPage: currentWarehousePage,
    totalItems: totalWarehouseItems,
    limit: warehouseLimit,
  } = pagination;

  const [search, setSearch] = useState("");
  const [localPage, setLocalPage] = useState(currentWarehousePage || 1);

  const fetchData = useCallback(
    (page: number, searchTerm: string) => {
      if (warehouseLimit) {
        dispatch(
          fetchStoreStockAdminViewPagination({
            page,
            limit: warehouseLimit,
            search: searchTerm,
          }),
        );
      }
    },
    [dispatch, warehouseLimit],
  );

  useEffect(() => {
    dispatch(getStorePriceByWarehouse());
  }, [dispatch]);

  const debouncedSearch = useMemo(
    () =>
      debounce((searchValue: string) => {
        fetchData(1, searchValue);
      }, 400),
    [fetchData],
  );

  useEffect(() => {
    fetchData(1, "");
  }, []);

  useEffect(() => {
    return () => debouncedSearch.cancel();
  }, [debouncedSearch]);

  useEffect(() => {
    if (currentWarehousePage && currentWarehousePage !== localPage) {
      setLocalPage(currentWarehousePage);
    }
  }, [currentWarehousePage, localPage]);

  const handleSearchChange = (value: string) => {
    setSearch(value);
    debouncedSearch(value);
  };

  const handlePageChange = (newPage: number) => {
    fetchData(newPage, search);
  };

  const noResultsMessage = useMemo(
    () =>
      search
        ? {
            title: "No raw materials found",
            subtitle: "Try a different search term or clear the search box.",
          }
        : {
            title: "No stock available",
            subtitle: "Stock will appear once raw materials are added.",
          },
    [search],
  );

  const totalStockForMaterial = useCallback((material: RawMaterialStock) => {
    return (material.stock || []).reduce((sum, w) => sum + w.quantity.value, 0);
  }, []);

  const downloadStockReport = (format: "xlsx" | "csv") => {
    if (!isExporting) {
      dispatch(downloadStoreStockReport({ format, search, date: globalDate }));
    }
  };

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-4 w-full">
      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 mb-6">
        <div className="bg-blue-50/50 px-6 py-3 border-b border-gray-100 rounded-t-2xl flex justify-between items-center">
          <h3 className="text-xl font-bold text-gray-800 flex items-center">
            <svg
              className="w-5 h-5 mr-2 text-blue-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M20 7l-1.5-1.5M4 7l1.5-1.5M20 17l-1.5 1.5M4 17l1.5 1.5M3 12h18M9 3v18m6-18v18"
              />
            </svg>
            Raw Material Store Stock
          </h3>

          <div className="flex items-center text-sm sm:text-lg font-semibold text-gray-700">
            <span className="text-gray-600 mr-2">Total Price:</span>

            <div className="flex items-center">
              {valueLoading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600 mr-1.5"></div>
                  <span className="text-blue-600 text-base">Loading...</span>
                </div>
              ) : (
                <span className="text-blue-700 bg-blue-100 px-3 py-1 rounded-full shadow-inner text-base">
                  {formatValue(grandTotal)}
                </span>
              )}

              {!valueLoading && (
                <Menu
                  as="div"
                  className="relative inline-block text-left ml-2 z-20"
                >
                  <MenuButton
                    className="flex items-center justify-center h-8 w-8 rounded-full text-blue-600 hover:bg-blue-200 transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-blue-500"
                    title="View warehouse wise Price breakdown"
                  >
                    <InformationCircleIcon
                      className="w-5 h-5"
                      aria-hidden="true"
                    />
                  </MenuButton>

                  <Transition
                    as={React.Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <MenuItems className="absolute right-0 z-20 mt-2 w-60 origin-top-right rounded-xl bg-white shadow-2xl ring-1 ring-black ring-opacity-5 focus:outline-none p-3 border border-gray-100">
                      <div className="py-1">
                        <h4 className="text-sm font-bold text-gray-700 px-3 pb-2 border-b border-gray-100 mb-2">
                          Price Breakdown by Warehouse
                        </h4>

                        {(warehouseValues as WarehouseValue[]).map(
                          (warehouse) => (
                            <div
                              key={warehouse.warehouseId}
                              className="flex justify-between items-center px-3 py-2 text-sm hover:bg-gray-50 rounded-lg transition-colors"
                            >
                              <span className="text-gray-600">
                                {warehouse.name}
                              </span>
                              <span className="font-bold text-gray-800">
                                {formatValue(warehouse.totalPrice)}
                              </span>
                            </div>
                          ),
                        )}

                        <div className="border-t border-gray-200 pt-2 mt-2 flex justify-between items-center px-3 font-extrabold text-base text-blue-700">
                          <span>Grand Total:</span>
                          <span>{formatValue(grandTotal)}</span>
                        </div>
                      </div>
                    </MenuItems>
                  </Transition>
                </Menu>
              )}
            </div>
          </div>
        </div>
        <div className="p-4 sm:p-6 flex justify-between items-center">
          <label htmlFor="store-search" className="sr-only">
            Search Raw Material Stock
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 flex items-center pl-2.5 pointer-events-none">
              <svg
                className="w-4 h-4 text-gray-400"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                />
              </svg>
            </div>
            <input
              id="store-search"
              type="text"
              placeholder="Search by name or SKU..."
              value={search}
              onChange={(e) => handleSearchChange(e.target.value)}
              className="w-full max-w-sm p-2 pl-8 border border-slate-300 rounded-lg focus:ring-blue-500 focus:border-blue-500 transition-all text-sm shadow-sm"
            />
          </div>

          <Menu as="div" className="relative inline-block text-left">
            <div>
              <MenuButton
                className={`inline-flex justify-center items-center rounded-lg border shadow-sm px-4 py-2 text-sm font-medium transition-all ${
                  isExporting
                    ? "bg-gray-200 text-gray-500 cursor-wait border-gray-300"
                    : "bg-white text-gray-700 hover:bg-gray-50 border-gray-300 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                }`}
                disabled={isExporting}
              >
                {isExporting ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-500 mr-2"></div>
                    Exporting...
                  </>
                ) : (
                  <>
                    <ArrowDownTrayIcon
                      className="w-4 h-4 mr-2"
                      aria-hidden="true"
                    />
                    Download Report
                    <ChevronDownIcon
                      className="w-5 h-5 ml-2 -mr-1"
                      aria-hidden="true"
                    />
                  </>
                )}
              </MenuButton>
            </div>

            <Transition
              as={React.Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <MenuItems className="absolute right-0 z-10 mt-2 w-40 origin-top-right rounded-md bg-white shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                <div className="py-1">
                  <MenuItem>
                    {({ active }) => (
                      <button
                        onClick={() => downloadStockReport("xlsx")}
                        disabled={isExporting}
                        className={`${
                          active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                        } block w-full text-left px-4 py-2 text-sm ${
                          isExporting ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                      >
                        Download XLSX
                      </button>
                    )}
                  </MenuItem>
                  <MenuItem>
                    {({ active }) => (
                      <button
                        onClick={() => downloadStockReport("csv")}
                        disabled={isExporting}
                        className={`${
                          active ? "bg-gray-100 text-gray-900" : "text-gray-700"
                        } block w-full text-left px-4 py-2 text-sm ${
                          isExporting ? "opacity-50 cursor-not-allowed" : ""
                        }`}
                      >
                        Download CSV
                      </button>
                    )}
                  </MenuItem>
                </div>
              </MenuItems>
            </Transition>
          </Menu>
        </div>
      </div>

      {warehouseLoading && (
        <div className="bg-blue-50 border border-blue-200 text-blue-700 p-3 mb-6 rounded-lg shadow-sm">
          <div className="flex items-center">
            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-blue-600 mr-2"></div>
            <span className="text-sm font-medium">
              Loading store stock data...
            </span>
          </div>
        </div>
      )}

      <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead>
              <tr className="bg-gray-100 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider">
                <th className="px-4 py-2 text-left w-1/5">
                  Raw Material (SKU)
                </th>
                <th className="px-4 py-2 w-[100px]">Total Stock</th>
                <th className="px-4 py-2 w-[120px]">Total Price</th>
                <th className="px-4 py-2 w-1/3">Quantity by Warehouse</th>
                <th className="px-4 py-2 w-1/3">Price by Warehouse</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {(!warehouseProducts || warehouseProducts.length === 0) &&
              !warehouseLoading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-8 text-center">
                    <div className="flex flex-col items-center justify-center text-gray-400">
                      <svg
                        className="w-12 h-12 mb-3 text-gray-300"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-8m-8 0H4"
                        />
                      </svg>
                      <p className="text-base font-semibold text-gray-500">
                        {noResultsMessage.title}
                      </p>
                      <p className="mt-1 text-sm text-gray-500">
                        {noResultsMessage.subtitle}
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                (warehouseProducts as RawMaterialStock[]).map((m) => {
                  const totalStock = totalStockForMaterial(m);
                  const unit = m.stock?.[0]?.quantity.unit || "";
                  const totalPrice = (m.stock || []).reduce(
                    (sum, w) => sum + (w.totalPrice || 0),
                    0,
                  );

                  return (
                    <tr
                      key={m._id}
                      className="hover:bg-gray-50 transition-colors duration-150"
                    >
                      <td className="px-4 py-3 text-left text-sm font-medium text-gray-900">
                        {m.name}
                        <span className="block text-xs text-gray-500 font-normal mt-0.5">
                          SKU: {m.sku}
                        </span>
                      </td>

                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <span className="inline-flex items-center justify-center bg-green-100 text-green-800 text-base font-bold px-2 py-1 rounded-lg min-w-[5rem]">
                          {totalStock.toFixed(2)}{" "}
                          <span className="ml-1 font-semibold">{unit}</span>
                        </span>
                      </td>

                      <td className="px-4 py-3 whitespace-nowrap text-center">
                        <span className="inline-flex items-center justify-center bg-purple-100 text-purple-800 text-base font-bold px-2 py-1 rounded-lg min-w-[5rem]">
                          {formatValue(totalPrice)}
                        </span>
                      </td>

                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1 justify-center">
                          {(m.stock || []).map((w) => (
                            <span
                              key={w.warehouse._id}
                              className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200"
                              title={`${w.warehouse.name} Stock: ${w.quantity.value} ${w.quantity.unit}`}
                            >
                              {w.warehouse.name || "Unknown"}:
                              <strong className="ml-1 text-blue-600">
                                {w.quantity.value.toFixed(2)} {w.quantity.unit}
                              </strong>
                            </span>
                          ))}
                        </div>
                      </td>

                      <td className="px-4 py-3">
                        <div className="flex flex-wrap gap-1 justify-center">
                          {(m.stock || []).map((w) => (
                            <span
                              key={`${w.warehouse._id}-price`}
                              className="inline-flex items-center px-2 py-1 rounded-lg text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200"
                              title={`${w.warehouse.name} Value: ${formatValue(
                                w.totalPrice,
                              )}`}
                            >
                              {w.warehouse.name || "Unknown"}:
                              <strong className="ml-1 text-purple-600 font-bold">
                                {formatValue(w.totalPrice)}
                              </strong>
                            </span>
                          ))}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {totalWarehousePages > 1 && !warehouseLoading && (
          <div className="p-3 flex justify-between items-center border-t border-gray-200 bg-gray-50 rounded-b-2xl">
            <p className="text-xs text-gray-600">
              Showing <strong>{currentWarehousePage}</strong> of{" "}
              <strong>{totalWarehousePages}</strong> pages (
              <strong>{totalWarehouseItems}</strong> items)
            </p>
            <div className="flex space-x-2">
              <button
                onClick={() => handlePageChange(localPage - 1)}
                disabled={localPage <= 1}
                className={`flex items-center px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                  localPage <= 1
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white text-blue-600 border-blue-300 hover:bg-blue-50"
                }`}
              >
                Previous
              </button>
              <button
                onClick={() => handlePageChange(localPage + 1)}
                disabled={localPage >= totalWarehousePages}
                className={`flex items-center px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                  localPage >= totalWarehousePages
                    ? "bg-gray-100 text-gray-400 cursor-not-allowed"
                    : "bg-white text-blue-600 border-blue-300 hover:bg-blue-50"
                }`}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default StoreStockPageByWarehouse;